import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fetchTemplate, openMailto, renderTemplate } from "@/lib/emailTemplates";
import { toast } from "@/lib/notify";
import RecipientPromptModal from "@/components/RecipientPromptModal";
import { PASTORAL_STATS } from "@/constants/testIds";
import {
  BarChart3,
  RefreshCw,
  Download,
  Send,
  Bell,
  TrendingUp,
  CheckCheck,
  Mail,
  ClipboardList as ClipboardListIcon,
} from "lucide-react";

function currentMonthStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

function monthLabel(ym) {
  const [y, m] = (ym || "").split("-");
  return `${MESES[parseInt(m, 10) - 1] || m} ${y}`;
}

function KpiCard({ label, value, icon: Icon, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className="flex items-start justify-between gap-3 border border-[#d9cfb8] bg-white p-4 rounded shadow-sm"
    >
      <div>
        <div className="text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">
          {label}
        </div>
        <div className="text-2xl font-serif text-[#1c1a14] tabular-nums">
          {value}
        </div>
      </div>
      <div
        className="rounded p-2 flex items-center justify-center"
        style={{ background: accent + "22", color: accent }}
      >
        <Icon size={18} />
      </div>
    </div>
  );
}

export default function PastoralStatsSection() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "administrador";

  const [month, setMonth] = useState(currentMonthStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [reminder, setReminder] = useState(null);
  const [pendingSend, setPendingSend] = useState(false);
  const [recipientModal, setRecipientModal] = useState(null); // { defaultTo } | null

  const loadReminder = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/stats/summary-reminder");
      setReminder(data);
    } catch {
      setReminder(null);
    }
  };

  const dismissReminder = async () => {
    try {
      await api.post("/stats/summary-reminder/dismiss");
      setReminder((r) => (r ? { ...r, due: false, dismissed: true } : r));
    } catch (e) {
      // Non-blocking: dismiss is idempotent, user can retry.
      console.warn("PastoralStats.dismissReminder failed", e);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/stats/pastoral?month=${month}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando estadísticas");
    } finally {
      setLoading(false);
    }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/stats/pastoral.pdf?month=${month}`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reporte_pastoral_${month}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error("Error generando PDF");
    } finally {
      setExporting(false);
    }
  };

  /**
   * Open the recipient modal. We never use `window.prompt` because it is
   * silently blocked inside sandboxed iframes (e.g. Emergent preview).
   */
  const openSendEmailModal = () => {
    if (!data) return;
    setRecipientModal({ defaultTo: me?.email || "" });
  };

  const sendEmailSummary = async (to) => {
    if (!data || !to) return;
    const recipients = to
      .split(",")
      .map((r) => r.trim())
      .filter((r) => r.includes("@"));
    if (recipients.length === 0) {
      toast.error("Ingresa al menos un correo válido");
      return;
    }
    const tpl = await fetchTemplate("monthly_summary");
    if (!tpl) {
      toast.error("No se pudo cargar la plantilla del resumen mensual");
      return;
    }
    const [yearStr, monthStr] = month.split("-");
    const mesLabel = MESES[parseInt(monthStr, 10) - 1] || monthStr;
    const top = data.emails.top_recipients
      .map((r, i) => `  ${i + 1}. ${r.recipient} (${r.count} envío${r.count !== 1 ? "s" : ""})`)
      .join("\n") || "  (sin envíos este mes)";
    const vars = {
      iglesia: "AJAR — Ministerio Árboles Junto al Río",
      mes: mesLabel,
      anio: yearStr,
      emails_total: data.emails.total,
      recordatorios_48h: data.emails.reminders_48h,
      solicitud_nueva: data.emails.request_new,
      decision_solicitud: data.emails.request_decision,
      emails_otros: data.emails.other,
      solicitudes_total: data.requests.total,
      aprobadas: data.requests.aprobado,
      rechazadas: data.requests.rechazado,
      pendientes: data.requests.pendiente ?? 0,
      top_destinatarios: top,
    };
    const subject = renderTemplate(tpl.subject, vars);
    const body = renderTemplate(tpl.body, vars);

    // Log once per recipient so the history shows the distribution
    Promise.all(
      recipients.map((rcpt) =>
        api
          .post("/mailto-logs", {
            template_key: "other",
            recipient: rcpt,
            subject,
            body_preview: body.slice(0, 500),
            context_label: `Resumen mensual pastoral — ${month}`,
          })
          .catch(() => {}),
      ),
    ).then(loadReminder);
    openMailto(recipients.join(","), subject, body);
    setRecipientModal(null);
  };

  useEffect(() => {
    queueMicrotask(load);
  }, [month]);

  useEffect(() => {
    queueMicrotask(loadReminder);
  }, []);

  // Auto-trigger the summary email after switching to the reminded month.
  useEffect(() => {
    if (pendingSend && data && month === reminder?.month) {
      setPendingSend(false);
      openSendEmailModal();
    }
  }, [pendingSend, data, month]);

  return (
    <section
      data-testid={PASTORAL_STATS.section}
      className="mb-6 border border-[#d9cfb8] bg-gradient-to-br from-[#fbf7ee] to-white p-5 rounded shadow-sm"
    >
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[#1c1a14]">
            <BarChart3 size={16} className="text-[#7a5b0f]" />
            <h3 className="font-serif text-base">Estadísticas pastorales</h3>
          </div>
          <p className="text-[11px] text-[#5d5849] mt-0.5">
            {isAdmin
              ? "Resumen de actividad de toda la iglesia."
              : "Resumen de tu actividad."}{" "}
            Selecciona el mes para ver datos históricos.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">
              Mes
            </label>
            <input
              data-testid={PASTORAL_STATS.monthInput}
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="border border-[#d9cfb8] bg-white px-2 py-1.5 text-[12px] rounded"
            />
          </div>
          <button
            type="button"
            data-testid={PASTORAL_STATS.refresh}
            onClick={load}
            className="inline-flex items-center gap-1 rounded border border-[#d9cfb8] bg-white px-3 py-1.5 text-[11px] uppercase tracking-widest text-[#1c1a14] hover:bg-[#fbf7ee]"
          >
            <RefreshCw size={12} /> Actualizar
          </button>
          <button
            type="button"
            data-testid={PASTORAL_STATS.exportPdf}
            onClick={exportPdf}
            disabled={exporting || loading || !data}
            className="inline-flex items-center gap-1 rounded border border-[#1c2a3a] bg-[#1c2a3a] px-3 py-1.5 text-[11px] uppercase tracking-widest text-white hover:bg-[#0f1a2a] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Download size={12} /> {exporting ? "Generando..." : "PDF"}
          </button>
          <button
            type="button"
            data-testid="pastoral-stats-email-summary"
            onClick={openSendEmailModal}
            disabled={loading || !data}
            title="Abre tu cliente de correo con el resumen del mes prellenado"
            className="inline-flex items-center gap-1 rounded border border-[#0f7a4a] bg-[#0f7a4a] px-3 py-1.5 text-[11px] uppercase tracking-widest text-white hover:bg-[#0a5a35] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Mail size={12} /> Enviar resumen
          </button>
        </div>
      </div>

      {isAdmin && reminder?.due && (
        <div
          data-testid={PASTORAL_STATS.reminderBanner}
          className="mb-3 flex items-center justify-between gap-3 flex-wrap rounded border border-[#d4a017] bg-[#fdf6e3] px-3 py-2.5"
        >
          <div className="flex items-center gap-2 text-[12px] text-[#7a5b0f]">
            <Bell size={14} className="shrink-0" />
            <span>
              Aún no has enviado el <strong>resumen pastoral de {monthLabel(reminder.month)}</strong>.
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid={PASTORAL_STATS.reminderSend}
              onClick={() => {
                setPendingSend(true);
                setMonth(reminder.month);
              }}
              className="inline-flex items-center gap-1 rounded border border-[#0f7a4a] bg-[#0f7a4a] px-3 py-1 text-[11px] uppercase tracking-widest text-white hover:bg-[#0a5a35]"
            >
              <Mail size={11} /> Enviar ahora
            </button>
            <button
              type="button"
              data-testid={PASTORAL_STATS.reminderDismiss}
              onClick={dismissReminder}
              className="rounded border border-[#d9cfb8] bg-white px-3 py-1 text-[11px] uppercase tracking-widest text-[#5d5849] hover:bg-[#fbf7ee]"
            >
              Descartar
            </button>
          </div>
        </div>
      )}

      {loading || !data ? (
        <div className="text-[12px] text-[#8b7e5e] italic py-4">
          Cargando estadísticas…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <KpiCard
              testid={PASTORAL_STATS.kpiEmailsTotal}
              label="Emails enviados"
              value={data.emails.total}
              icon={Send}
              accent="#7a5b0f"
            />
            <KpiCard
              testid={PASTORAL_STATS.kpiReminders}
              label="Recordatorios 48h"
              value={data.emails.reminders_48h}
              icon={Bell}
              accent="#1c2a3a"
            />
            <KpiCard
              testid={PASTORAL_STATS.kpiRequestsTotal}
              label="Solicitudes"
              value={data.requests.total}
              icon={ClipboardListIcon}
              accent="#9b6c0f"
            />
            <KpiCard
              testid={PASTORAL_STATS.kpiApproved}
              label="Aprobadas / Rechazadas"
              value={`${data.requests.aprobado} / ${data.requests.rechazado}`}
              icon={CheckCheck}
              accent="#0f6b4a"
            />
          </div>

          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="border border-[#e8e1cd] bg-white p-3 rounded">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-2">
                <TrendingUp size={12} className="text-[#7a5b0f]" />
                Desglose de emails
              </div>
              <dl className="text-[12px] space-y-1">
                <div className="flex justify-between"><dt className="text-[#5d5849]">Recordatorios 48h</dt><dd className="tabular-nums">{data.emails.reminders_48h}</dd></div>
                <div className="flex justify-between"><dt className="text-[#5d5849]">Nueva solicitud</dt><dd className="tabular-nums">{data.emails.request_new}</dd></div>
                <div className="flex justify-between"><dt className="text-[#5d5849]">Decisión solicitud</dt><dd className="tabular-nums">{data.emails.request_decision}</dd></div>
                <div className="flex justify-between"><dt className="text-[#5d5849]">Otros</dt><dd className="tabular-nums">{data.emails.other}</dd></div>
                <div className="flex justify-between border-t border-[#e8e1cd] pt-1 mt-1 font-medium"><dt>Total</dt><dd className="tabular-nums">{data.emails.total}</dd></div>
              </dl>
            </div>

            <div
              data-testid={PASTORAL_STATS.topList}
              className="border border-[#e8e1cd] bg-white p-3 rounded"
            >
              <div className="text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-2">
                Top 5 destinatarios del mes
              </div>
              {data.emails.top_recipients.length === 0 ? (
                <div
                  data-testid={PASTORAL_STATS.emptyTop}
                  className="text-[12px] text-[#8b7e5e] italic"
                >
                  Sin envíos en este mes.
                </div>
              ) : (
                <ol className="space-y-1 text-[12px]">
                  {data.emails.top_recipients.map((r, i) => (
                    <li
                      key={r.recipient}
                      data-testid={PASTORAL_STATS.topRow(i)}
                      className="flex justify-between items-center"
                    >
                      <span className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#fbf7ee] border border-[#d9cfb8] text-[10px] text-[#7a5b0f]">
                          {i + 1}
                        </span>
                        <span className="text-[#1c1a14] break-all">{r.recipient}</span>
                      </span>
                      <span className="tabular-nums text-[#5d5849]">{r.count}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </>
      )}

      {recipientModal && (
        <RecipientPromptModal
          testid="pastoral-stats-recipient-modal"
          title="Enviar resumen pastoral"
          subtitle={monthLabel(month)}
          defaultValue={recipientModal.defaultTo}
          placeholder="pastor@ajar.com, secretaria@ajar.com"
          onCancel={() => setRecipientModal(null)}
          onConfirm={(value) => sendEmailSummary(value)}
        />
      )}
    </section>
  );
}
